import EXIF from 'exif-js'

export function extractOrientationValue(a: ArrayBuffer): number {
  const view = new DataView(a)

  const length = view.byteLength
  let offset = 2
  let orientation = -1
  while (offset < length) {
    if (view.getUint16(offset + 2, false) <= 8) {
      orientation = -1
    }
    const marker = view.getUint16(offset, false)
    offset += 2
    // eslint-disable-next-line
    if (marker === 0xffe1) {
      if (view.getUint32((offset += 2), false) !== 0x45786966) {
        orientation = -1
        break
      }

      const little = view.getUint16((offset += 6), false) === 0x4949
      offset += view.getUint32(offset + 4, little)
      const tags = view.getUint16(offset, little)
      offset += 2
      for (let i = 0; i < tags; i++) {
        if (view.getUint16(offset + i * 12, little) === 0x0112) {
          orientation = view.getUint16(offset + i * 12 + 8, little)
        }
      }
      if (orientation > 0) {
        break
      }
      // eslint-disable-next-line
    } else if ((marker & 0xff00) !== 0xff00) {
      break
    } else {
      offset += view.getUint16(offset, false)
    }
  }
  return orientation
}

export function base64ToFile(dataURI: string, file: File) {
  let byteString

  if (dataURI.split(',')[0].includes('base64')) {
    byteString = atob(dataURI.split(',')[1])
  } else {
    byteString = decodeURI(dataURI.split(',')[1])
  }

  const mimestring = dataURI
    .split(',')[0]
    .split(':')[1]
    .split(';')[0]

  const content = []
  for (let i = 0; i < byteString.length; i++) {
    content[i] = byteString.charCodeAt(i)
  }

  return new File([new Uint8Array(content)], file.name, {
    type: mimestring
  })
}

export const blobUrlToFile = (
  blobUrl: string,
  title = 'image'
): Promise<File> =>
  new Promise(resolve => {
    fetch(blobUrl).then(async res => {
      const blob = await res.blob()
      const file = new File([blob], `${title}.png`, {
        type: 'image/png',
        lastModified: new Date().getTime()
      })
      resolve(file)
    })
  })

export function readImageMetadata(
  file: File
): Promise<{ orientation: number; make?: string; model?: string }> {
  return new Promise(resolve => {
    try {
      const imageOrientationSupported =
        typeof document !== 'undefined'
          ? (document.createElement('img').style as any).imageOrientation !==
            undefined
          : false

      if (imageOrientationSupported) {
        resolve({ orientation: -1 })
        return
      }

      const orientationReader = new FileReader()
      orientationReader.onload = (e: any) => {
        try {
          const make = EXIF.readFromBinaryFile(e.target?.result)?.Make
          resolve({
            orientation: extractOrientationValue(e.target?.result),
            make
          })
        } catch (e) {
          resolve({ orientation: -1 })
        }
      }
      orientationReader.readAsArrayBuffer(file)
    } catch {
      resolve({ orientation: -1 })
    }
  })
}

export function resizeImage({
  canvas,
  initWidth,
  initHeight,
  maxResizeWidth,
  maxResizeHeight,
  minWidth,
  minHeight,
  orientation,
  image,
  eventWidth,
  eventHeight,
  file,
  make,
  backgroundColor = '#000000' // default fallback
}: {
  canvas: HTMLCanvasElement
  initWidth: number
  initHeight: number
  maxResizeWidth: number
  maxResizeHeight: number
  minWidth: number
  minHeight: number
  orientation: number
  image: any
  eventWidth: any
  eventHeight: any
  file: File
  make?: string
  backgroundColor?: string
}) {
  function getImageAndThumb(canvas: HTMLCanvasElement) {
    const finalBase64 = canvas.toDataURL('image/jpeg')

    return {
      file: base64ToFile(finalBase64, file),
      thumb: canvas.toDataURL('image/jpeg'),
      width: canvas.width,
      height: canvas.height
    }
  }
  let width = initWidth
  let height = initHeight
  canvas.width = width
  canvas.height = height
  canvas.style.opacity = '0'

  let resized = false

  if (width > maxResizeWidth) {
    height *= maxResizeWidth / width
    width = maxResizeWidth
    resized = true
  }
  if (height > maxResizeHeight) {
    width *= maxResizeHeight / height
    height = maxResizeHeight
    resized = true
  }

  // repeat for minimum resizes
  if (width < minWidth) {
    height *= minWidth / width
    width = minWidth
    resized = true
  }
  if (height < minHeight) {
    width *= minHeight / height
    height = minHeight
    resized = true
  }

  if (resized) {
    canvas.width = width
    canvas.height = height
  }
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return getImageAndThumb(canvas)
  }

  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // transform context before drawing image
  if (orientation > 1) {
    switch (orientation) {
      case 2:
        // flip horizontal
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 3:
        // rotate 180 degrees left
        ctx.translate(width, height)
        ctx.rotate(-Math.PI)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 4:
        // vertical flip
        ctx.translate(0, height)
        ctx.scale(1, -1)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, width, height)
        break // supported
      case 5:
        // 90 rotate right + flip horizontal
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.translate(width, 0)
        ctx.rotate((90 * Math.PI) / 180)

        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break // supported
      case 7:
        // horizontal flip + 90 rotate right
        ctx.translate(width, 0)
        ctx.scale(-1, 1)
        ctx.translate(0, height)
        ctx.rotate((-90 * Math.PI) / 180)

        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break
      case 8:
        ctx.translate(0, height)
        ctx.rotate((-90 * Math.PI) / 180)
        ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
        break
    }
    // @Hack for ipad images
    if (orientation === 6 && make === 'Apple') {
      ctx.translate(0, height)
      ctx.rotate((-90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
    } else if (orientation === 6) {
      ctx.translate(width, 0)
      ctx.rotate((90 * Math.PI) / 180)
      ctx.drawImage(image, 0, 0, eventWidth, eventHeight, 0, 0, height, width)
    }
  }

  // if it has orientation it will be drawn in the orientation case
  if (orientation <= 1) {
    if (resized) {
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height)
    } else {
      canvas.width = image.width
      canvas.height = image.height
      ctx.drawImage(image, 0, 0, image.width, image.height)
    }
  }

  return getImageAndThumb(canvas)
}
